import type { AuthRouterContext } from '@my-langguth/auth-ui'
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  useMatches,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { AppShell, ProductSwitcher } from '@my-langguth/shell'
import { getProductSwitcherUrls } from '@/lib/product-switcher-urls'
import { useTranslation } from 'react-i18next'
import { Toaster } from 'sonner'

import { CustomerIdProvider } from '@/components/customer-id-provider'
import { CustomerSelect } from '@/components/customer-select'
import { UserMenu } from '@/components/user-menu'
import { useAuth } from '@/hooks/useAuth'
import { requireChecklistenAuth } from '@/lib/checklisten-auth-guard'
import { parseChecklistRunsSearch } from '@/lib/checklist-runs-search'

import '../styles.css'

function RootError({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const message =
    error instanceof Error
      ? error.message
      : String(error ?? t('common.unknownError'))
  return (
    <div className="min-h-svh bg-background p-6 text-foreground">
      <h1 className="text-lg font-semibold text-destructive">{t('common.error')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  )
}

/** No session check — login and password-reset flows */
const PUBLIC_ROUTE_IDS = new Set([
  '/login',
  '/request-password-reset',
  '/reset-password',
])

export const Route = createRootRouteWithContext<AuthRouterContext>()({
  beforeLoad: async ({ context, location, matches }) => {
    const visitingPublicRoute = matches.some((m) =>
      PUBLIC_ROUTE_IDS.has(String(m.routeId)),
    )
    if (visitingPublicRoute) return

    await requireChecklistenAuth({ context, location })
  },
  component: RootComponent,
  errorComponent: RootError,
})

function RootLayoutChrome() {
  const { t } = useTranslation()
  const matches = useMatches()
  const hideAppChrome = matches.some((m) => PUBLIC_ROUTE_IDS.has(m.routeId))
  const { isAuthenticated, user } = useAuth()

  const { shopUrl, checklistenUrl, flowManagerUrl } = getProductSwitcherUrls()

  const header = hideAppChrome ? null : (
    <header className="flex flex-wrap items-end gap-3">
      <div className="order-1 flex flex-wrap items-end gap-4">
        <div className="flex flex-wrap items-end gap-2">
          <ProductSwitcher
            current="checklisten"
            shopUrl={shopUrl}
            checklistenUrl={checklistenUrl}
            flowManagerUrl={
              user?.role === 'langguth_admin' ? flowManagerUrl : undefined
            }
            labels={{
              menuTitle: t('appSwitcher.menuTitle'),
              shop: t('appSwitcher.shop'),
              checklists: t('appSwitcher.checklists'),
              flowManager: t('appSwitcher.flowManager'),
              triggerAria: t('appSwitcher.triggerAria'),
              currentBadge: t('appSwitcher.currentBadge'),
            }}
          />
          <h1 className="text-2xl font-semibold">
            <Link to="/" className="text-foreground">
              <span className="text-green-600">myLangguth</span> {t('nav.appTitle')}
            </Link>
          </h1>
        </div>
        <nav className="ml-1 flex flex-wrap items-end gap-3 border-l border-border pl-4 text-lg font-medium">
          <Link
            to="/"
            className="text-muted-foreground data-[status=active]:text-foreground"
            activeOptions={{ exact: true }}
          >
            {t('nav.dashboard')}
          </Link>
          <Link
            to="/checklist-runs"
            search={parseChecklistRunsSearch({})}
            className="text-muted-foreground data-[status=active]:text-foreground"
          >
            {t('nav.runs')}
          </Link>
        </nav>
      </div>

      <div className="order-2 ml-auto flex items-end md:order-3 md:ml-0">
        {isAuthenticated ? <UserMenu /> : null}
      </div>

      <div className="order-3 basis-full md:order-2 md:ml-auto md:basis-auto">
        <CustomerSelect />
      </div>
    </header>
  )

  return (
    <>
      <Toaster richColors theme="light" />
      <AppShell header={header} sidebar={false}>
        <Outlet />
      </AppShell>
      <TanStackDevtools
        config={{
          position: 'bottom-right',
        }}
        plugins={[
          {
            name: 'TanStack Router',
            render: <TanStackRouterDevtoolsPanel />,
          },
        ]}
      />
    </>
  )
}

function RootComponent() {
  return (
    <CustomerIdProvider>
      <RootLayoutChrome />
    </CustomerIdProvider>
  )
}
